import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface UseBulkOperationsOptions<T> {
    /** Array of selected item IDs */
    selectedItems: string[];
    /** Function to delete a single item */
    deleteFn: (id: string) => Promise<void>;
    /** Callback after successful bulk delete */
    onDeleteSuccess?: () => void;
    /** Callback on delete error */
    onDeleteError?: (error: Error) => void;
    /** Entity name for toast messages (singular). Default: 'registro' */
    entityName?: string;
    /** Entity name for toast messages (plural). Auto-generated if not provided */
    entityNamePlural?: string;
}

export interface UseBulkOperationsReturn {
    /** Whether bulk delete dialog is open */
    isBulkDeleteDialogOpen: boolean;
    /** Open the bulk delete confirmation dialog */
    openBulkDeleteDialog: () => void;
    /** Close the bulk delete confirmation dialog */
    closeBulkDeleteDialog: () => void;
    /** Execute the bulk delete operation */
    confirmBulkDelete: () => Promise<void>;
    /** Whether a delete operation is in progress */
    isDeleting: boolean;

    /** Whether bulk edit modal is open */
    isBulkEditModalOpen: boolean;
    /** Open the bulk edit modal */
    openBulkEditModal: () => void;
    /** Close the bulk edit modal */
    closeBulkEditModal: () => void;

    /** Generated delete dialog title */
    deleteDialogTitle: string;
    /** Generated delete dialog description */
    deleteDialogDescription: string;
}

/**
 * useBulkOperations - Hook for managing bulk edit/delete operations
 * 
 * @example
 * const {
 *   isBulkDeleteDialogOpen,
 *   openBulkDeleteDialog,
 *   confirmBulkDelete,
 *   isDeleting,
 *   deleteDialogTitle,
 *   deleteDialogDescription,
 * } = useBulkOperations({
 *   selectedItems: selectedExpenses,
 *   deleteFn: (id) => deleteExpenseMutation.mutateAsync(id),
 *   onDeleteSuccess: () => clearSelection(),
 *   entityName: 'gasto',
 * });
 */
export function useBulkOperations<T>({
    selectedItems,
    deleteFn,
    onDeleteSuccess,
    onDeleteError,
    entityName = 'registro',
    entityNamePlural,
}: UseBulkOperationsOptions<T>): UseBulkOperationsReturn {
    const { toast } = useToast();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
    const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);

    const count = selectedItems.length;
    const plural = entityNamePlural || `${entityName}(s)`;

    const openBulkDeleteDialog = useCallback(() => {
        setIsBulkDeleteDialogOpen(true);
    }, []);

    const closeBulkDeleteDialog = useCallback(() => {
        setIsBulkDeleteDialogOpen(false);
    }, []);

    const openBulkEditModal = useCallback(() => {
        setIsBulkEditModalOpen(true);
    }, []);

    const closeBulkEditModal = useCallback(() => {
        setIsBulkEditModalOpen(false);
    }, []);

    const confirmBulkDelete = useCallback(async () => {
        setIsDeleting(true);
        try {
            await Promise.all(selectedItems.map((id) => deleteFn(id)));

            toast({
                title: 'Éxito',
                description: `Se eliminaron ${count} ${plural} correctamente`,
            });

            setIsBulkDeleteDialogOpen(false);
            onDeleteSuccess?.();
        } catch (error) {
            console.error(`Error deleting selected ${plural}:`, error);
            toast({
                title: 'Error',
                description: `Error al eliminar los ${plural} seleccionados`,
                variant: 'destructive',
            });
            onDeleteError?.(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setIsDeleting(false);
        }
    }, [selectedItems, deleteFn, count, plural, toast, onDeleteSuccess, onDeleteError]);

    const deleteDialogTitle = `¿Eliminar ${count} ${plural}?`;
    const deleteDialogDescription = `Esta acción no se puede deshacer. Se eliminarán permanentemente ${count} ${plural}.`;

    return {
        isBulkDeleteDialogOpen,
        openBulkDeleteDialog,
        closeBulkDeleteDialog,
        confirmBulkDelete,
        isDeleting,

        isBulkEditModalOpen,
        openBulkEditModal,
        closeBulkEditModal,

        deleteDialogTitle,
        deleteDialogDescription,
    };
}

export default useBulkOperations;
